import React, { useEffect } from "react";
import FilesDropZone from "components/FilesDropZone";
import DownloadModelRequirements from "./DownloadModelRequirements";
import SplitQuantityCheckbox from "./SplitQuantityCheckbox";
import IFCFileIcon from "assets/images/icons/ifc-file-icon.svg";
import CSVFileIcon from "assets/images/icons/csv-file-icon.svg";
import { UploadType } from ".";
import CircularProgressBar from "components/CircularProgressBar";
import { useParams } from "react-router";
import useToast from "hooks/useToast";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { uploadFileStore } from "./UploadFileStore";
import { usePostHog } from "@posthog/react";

export default observer(function UploadFile(props: {
  uploadType: UploadType | null;
  setClose?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { id } = useParams();
  const toast = useToast();
  const { t } = useTranslation();
  const posthog = usePostHog();

  useEffect(() => {
    return () => {
      uploadFileStore.setCurrentFile(undefined);
      uploadFileStore.setSplitQuantityIntoObjects(false);
    };
  }, []);

  const handleFileSelected: React.Dispatch<
    React.SetStateAction<File | undefined>
  > = (fileOrUpdater) => {
    const file =
      typeof fileOrUpdater === "function"
        ? fileOrUpdater(uploadFileStore.currentFile)
        : fileOrUpdater;
    if (!file || !id || !props.uploadType) return;
    const uploadType = props.uploadType;
    uploadFileStore.setCurrentFile(file);
    (async () => {
      try {
        await uploadFileStore.uploadFile(id, uploadType);
        toast(t("buildingMaterialResources.uploadSucces"), "success");
        posthog.capture("file_uploaded", {
          building_id: id,
          upload_type: uploadType,
          file_name: file.name,
          file_size_bytes: file.size,
        });
      } catch (err: unknown) {
        console.error(err);
        if (err instanceof Error) {
          toast(err.message, "warning");
        }
      }
      props.setClose?.(false);
    })();
  };

  function uploadingView() {
    return (
      <div className="py-4">
        <div className="bg-white flex w-full py-1 items-center justify-center">
          <CircularProgressBar percent={uploadFileStore.uploadProgress} />
        </div>
        <div className="mt-4 text-center text-md text-gray-700 h-6">
          {uploadFileStore.uploadProgress === 100
            ? t("buildingMaterialResources.processing")
            : null}
        </div>
      </div>
    );
  }

  function uploadExcelOrCSV() {
    return (
      <>
        <DownloadModelRequirements uploadType={props.uploadType} />
        {props.uploadType === "CSV" && <SplitQuantityCheckbox />}
        <FilesDropZone
          setFile={handleFileSelected}
          icon={props.uploadType === "IFC" ? IFCFileIcon : CSVFileIcon}
          acceptedTypes={
            props.uploadType === "IFC"
              ? [".ifc", ".zip"]
              : [".csv", ".xlsx", ".zip"]
          }
          showAlertForIFC
        />
      </>
    );
  }

  if (uploadFileStore.isUploading) return uploadingView();
  return uploadExcelOrCSV();
});
